import type { ReactNode } from 'react';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from '@/components/ui/context-menu';
import { useTranslation } from '@/i18n/LanguageContext';
import type { BlockType } from '@/types/workflow';

export interface CanvasToolType {
  type: BlockType;
  label: string;
  icon?: string;
}

interface Props {
  children: ReactNode;
  /**
   * Fired on contextmenu before the menu opens so the parent can capture the
   * cursor position for subsequent "Add block" placement.
   */
  onContextMenuCapture: (e: React.MouseEvent) => void;
  toolTypes: CanvasToolType[];
  onAddBlock: (type: BlockType) => void;
  onResetZoom?: () => void;
  onFitToContent?: () => void;
  disabled?: boolean;
}

/**
 * Right-click menu for the canvas background. Opens when the user right-clicks
 * an empty area — child ContextMenus on blocks/layers still take priority
 * because Radix stops propagation once their menu opens.
 */
export function CanvasContextMenu({
  children,
  onContextMenuCapture,
  toolTypes,
  onAddBlock,
  onResetZoom,
  onFitToContent,
  disabled,
}: Props) {
  const { t } = useTranslation();

  if (disabled) return <>{children}</>;

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div onContextMenu={onContextMenuCapture}>
          {children}
        </div>
      </ContextMenuTrigger>
      <ContextMenuContent className="w-56">
        <ContextMenuSub>
          <ContextMenuSubTrigger>{t('addBlock') || 'Add block'}</ContextMenuSubTrigger>
          <ContextMenuSubContent className="w-56 max-h-80 overflow-y-auto">
            {toolTypes.length === 0 && (
              <ContextMenuItem disabled>
                {t('noBlockTypes') || 'No block types available'}
              </ContextMenuItem>
            )}
            {toolTypes.map((tool) => (
              <ContextMenuItem
                key={`${tool.type}-${tool.label}`}
                onSelect={() => onAddBlock(tool.type)}
              >
                {tool.label}
              </ContextMenuItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>

        {(onResetZoom || onFitToContent) && <ContextMenuSeparator />}

        {onFitToContent && (
          <ContextMenuItem onSelect={onFitToContent}>
            {t('fitToContent') || 'Fit to content'}
          </ContextMenuItem>
        )}
        {onResetZoom && (
          <ContextMenuItem onSelect={onResetZoom}>
            {t('resetZoom') || 'Reset zoom (Ctrl+0)'}
          </ContextMenuItem>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
